import React from 'react';
import { Star, Quote, ExternalLink } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Button } from './ui/button';

const googleReviews = [
  {
    id: 1,
    name: "Aurélie B.",
    date: "Il y a 2 mois",
    text: "Rapide, efficace, ne pousse pas à la dépense. CLIC58 m'a été conseillé et je recommande à mon tour ! Merci Yohan et à très bientôt.",
    rating: 5,
    avatar: "A"
  },
  {
    id: 2,
    name: "Luc G.",
    date: "Il y a 3 mois",
    text: "Un grand merci, une grande aide, indispensable. Merci d'avoir sauvé la vie de mon ordinateur / mes données ! Et également merci de la formation rapide afin de ne pas réitérer mes erreurs !",
    rating: 5,
    avatar: "L"
  },
  {
    id: 3,
    name: "Muriel J.",
    date: "Il y a 1 mois",
    text: "Un grand merci à CLIC58 pour avoir réactivé l'iPhone de ma fille ! Votre ordi est en panne ou rame, votre mobile est bloqué, dépannage assuré en un temps record pour une somme dérisoire !",
    rating: 5,
    avatar: "M"
  },
  {
    id: 4,
    name: "Philippe D.",
    date: "Il y a 2 semaines",
    text: "Excellent service ! Yohan est très professionnel et prend le temps d'expliquer. Mon ordinateur fonctionne comme au premier jour. Je recommande vivement !",
    rating: 5,
    avatar: "P"
  },
  {
    id: 5,
    name: "Catherine M.",
    date: "Il y a 1 mois",
    text: "Très satisfaite de l'accompagnement pour mon nouveau smartphone. Le transfert de données a été fait rapidement et les explications étaient claires. Merci !",
    rating: 5,
    avatar: "C"
  },
  {
    id: 6,
    name: "Jean-Pierre R.",
    date: "Il y a 3 semaines",
    text: "Service impeccable ! Installation à domicile rapide et soignée. L'assistance gratuite est un vrai plus. Je ne regrette pas d'avoir choisi CLIC58.",
    rating: 5,
    avatar: "J"
  }
];

const GoogleReviewsSection = () => {
  const [emblaRef] = useEmblaCarousel(
    { 
      loop: true, 
      align: 'start',
      slidesToScroll: 1
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );

  return (
    <section id="avis" className="py-24 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-cyan/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-blue/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full shadow-md mb-4">
            <img 
              src="https://www.google.com/favicon.ico" 
              alt="Google" 
              className="w-5 h-5"
            />
            <span className="text-gray-700 dark:text-gray-200 font-semibold text-sm">Avis Google</span>
            <div className="flex items-center gap-1 ml-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-gray-600 dark:text-gray-300 font-bold ml-1">5.0</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Ce que nos clients
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-blue to-primary-cyan"> disent de nous</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Plus de 2000 clients satisfaits nous font confiance depuis 2019.
          </p>
        </div>

        {/* Carousel */}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6">
            {googleReviews.map((review) => (
              <div
                key={review.id}
                className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0"
              >
                <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 h-full mx-2 hover:shadow-xl transition-shadow duration-300">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-blue to-primary-cyan rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {review.avatar}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{review.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{review.date}</p>
                      </div>
                    </div>
                    <img 
                      src="https://www.google.com/favicon.ico" 
                      alt="Google" 
                      className="w-6 h-6"
                    />
                  </div>
                  
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <div className="relative">
                    <Quote className="absolute -top-2 -left-2 w-8 h-8 text-primary-cyan/20" />
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed pl-4">
                      {review.text}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA to Google */}
        <div className="mt-12 text-center">
          <a
            href="https://www.google.com/search?q=clic+58+corbigny+avis"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 px-8 py-6 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3 mx-auto">
              <img 
                src="https://www.google.com/favicon.ico" 
                alt="Google" 
                className="w-5 h-5"
              />
              Voir tous nos avis Google
              <ExternalLink className="w-5 h-5" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
